import { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../UserContext";
import axios from "axios";
// Import icons for the password visibility toggle
import { FaLock, FaKey, FaEye, FaEyeSlash } from "react-icons/fa";
import "./styles.css";

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useContext(UserContext);
  const location = useLocation();

  const { phoneNumber } = location.state || {};

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // New state to manage password visibility
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // If there's no token, the user isn't authenticated to reset their password.
    if (!token) {
      console.log("No auth token in context. Redirecting.");
      // Send them back to the start of the forgot password flow
      navigate("/forgot-password");
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!phoneNumber) {
      console.log("No phone number provided. Redirecting to login.");
      navigate("/login");
    }
  }, [phoneNumber, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!password || !confirmPassword) {
      setError(t("Please fill in both fields."));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("Passwords do not match."));
      return;
    }

    setIsLoading(true);

    const requestBody = {
      newPassword: password,
      phoneNumber: phoneNumber,
      isEmail: false,
    };

    try {
      await axios.post("/api/user/resetPassword", requestBody, {
        headers: { Authorization: `${token}` },
      });
      setMessage(
        t("Password has been reset successfully! Redirecting to login...")
      );
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        t("Failed to reset password. Please try again.");
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <div className="reset-password-icon-container">
          <FaKey className="reset-password-icon" />
        </div>
        <h1 className="reset-password-header">{t("Change Password")}</h1>
        <form onSubmit={handleSubmit}>
          <div className="input-wrapper">
            <FaLock className="input-icon" />
            <input
              // Type is now dynamic based on showPassword state
              type={showPassword ? "text" : "password"}
              placeholder={t("New Password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="reset-password-input"
              required
            />
            {/* Password visibility toggle icon */}
            <span
              onClick={() => setShowPassword(!showPassword)}
              className="password-toggle-icon"
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>
          <div className="input-wrapper">
            <FaLock className="input-icon" />
            <input
              // Type is also dynamic here
              type={showPassword ? "text" : "password"}
              placeholder={t("Confirm New Password")}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="reset-password-input"
              required
            />
            {/* Password visibility toggle icon */}
            <span
              onClick={() => setShowPassword(!showPassword)}
              className="password-toggle-icon"
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>
          {error && <p className="error-message">{error}</p>}
          {message && <p className="success-message">{message}</p>}
          {/* Button now uses the new, unique class name */}
          <button
            type="submit"
            className="reset-password-btn"
            disabled={isLoading}
          >
            {isLoading ? t("Saving...") : t("Save")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
